<template>
  <div class="space-y-16 lg:space-y-24 py-12">
    <TContainer>
      <THeading
        :level="3"
        class="mb-2"
      >
        {{ pageHeading }}
      </THeading>

      <p class="font-semibold">
        {{ t('subtitle') }}
      </p>

      <AppMosaicImages
        v-if="parkMosaicImages?.length"
        :images="parkMosaicImages"
        aspect-ratio="21/9"
        class="mt-6"
      />

      <TBreadcrumb
        v-if="breadcrumbs?.length"
        :items="breadcrumbs"
        class="mt-4 lg:justify-end"
      />
    </TContainer>

    <TContainer
      v-if="data?.park?.Description"
      size="xl"
      class="space-y-4"
    >
      <THeading
        :level="3"
        tag="h2"
      >
        {{ t('aboutTitle', { name: data?.park?.Name ?? '' }) }}
      </THeading>

      <AppCollapsibleContent
        :content="data.park.Description"
        :line-clamp="10"
        is-show-more-button
        more-button-variant="link"
      />
    </TContainer>

    <TContainer
      v-if="data?.park?.RvFacts?.length"
      size="xl"
      class="space-y-4"
    >
      <THeading
        :level="3"
        tag="h2"
      >
        {{ t('rvFactsTitle', { name: data?.park?.Name ?? '' }) }}
      </THeading>

      <TTable>
        <template #thead>
          <tr class="border-b border-gray-200 text-left">
            <th class="py-2 pr-4 font-medium">
              {{ t('rvFactsLabel') }}
            </th>
            <th class="py-2 font-medium">
              {{ t('rvFactsValue') }}
            </th>
          </tr>
        </template>
        <tr
          v-for="fact in data.park.RvFacts"
          :key="fact.Id"
          class="border-b border-gray-100"
        >
          <td class="py-2 pr-4 font-medium align-top">
            {{ fact.Label }}
          </td>
          <td class="py-2 align-top">
            {{ fact.Value }}
            <a
              v-if="fact.Source"
              :href="fact.Source"
              target="_blank"
              rel="nofollow noopener noreferrer"
              class="inline-block ml-2 text-xs"
            >
              <TIcon
                :icon="['fal', 'external-link']"
                size="sm"
              />
            </a>
          </td>
        </tr>
      </TTable>
    </TContainer>

    <TContainer
      v-if="gridRvs?.length"
      size="xl"
      class="space-y-8"
    >
      <THeading
        tag="h2"
        :level="2"
      >
        {{ t('rvsGridTitle', { parkName: data?.park?.Name ?? '' }) }}
      </THeading>

      <Grid
        :max-results="gridRvs?.length ?? pageSize"
        :sm-cols="2"
        :lg-cols="3"
        :rows="6"
        :sm-rows="4"
        :lg-rows="3"
      >
        <template #default="{ getItemClass }">
          <CardRv
            v-for="(rv, index) in gridRvs"
            :key="rv.Id"
            :index="index"
            search-request-id=""
            :rv="rv"
            no-lazy-load
            cta="poi-rvs-grid"
            discovery-source="POI"
            :class="getItemClass(index)"
            is-seo-variant
            @visible:rv="queueViewableImpression"
          />
        </template>
      </Grid>

      <div class="flex justify-center">
        <TButton
          variant="highlight"
          class="w-full sm:w-auto"
          @click="navigateToRvSearch"
        >
          {{ t('browseRVRentals') }}
        </TButton>
      </div>
    </TContainer>

    <ExperimentComponent experiment-key="rvz26351UpdatedBrandDifferentiatorCards">
      <template #enabled>
        <TContainer
          size="xl"
          class="mb-8"
        >
          <GeneralBrandPillars focus="guest" />
        </TContainer>
      </template>
    </ExperimentComponent>

    <TContainer
      v-if="data?.park?.BestTimeToVisit || data?.park?.Weather?.length"
      size="xl"
      class="space-y-4"
    >
      <THeading
        :level="3"
        tag="h2"
      >
        {{ t('bestTimeToVisitTitle', { name: data?.park?.Name ?? '' }) }}
      </THeading>

      <p v-if="data?.park?.BestTimeToVisit">
        {{ data.park.BestTimeToVisit }}
      </p>

      <RvRentalsParkSeasonalityWeather
        v-if="sortedWeather.length"
        :monthly-weather="sortedWeather"
        :use-celsius="data.park?.Country === 'CA'"
        class="mt-8"
      />
    </TContainer>

    <TContainer
      v-if="data?.park?.ThingsToDo?.length"
      size="xl"
      class="space-y-4"
    >
      <THeading
        :level="3"
        tag="h2"
      >
        {{ t('thingsToDoTitle', { name: data?.park?.Name ?? '' }) }}
      </THeading>

      <TListContainer variant="divide">
        <TListItem
          v-for="item in data.park.ThingsToDo"
          :key="item.Id"
          class="py-4 space-y-2"
        >
          <div class="flex items-center justify-between gap-2 font-medium">
            <div>
              {{ item.Name }}
              <TBadge
                v-if="item.Difficulty"
                size="sm"
                class="ml-2"
              >
                {{ item.Difficulty }}
              </TBadge>
            </div>

            <TBadge
              v-if="item.HasRvParking"
              variant="success"
              size="sm"
            >
              {{ t('thingsToDoRvParking') }}
            </TBadge>
          </div>

          <p class="text-gray-500 text-sm leading-relaxed">
            {{ item.Description }}

            <a
              v-if="item.Source"
              :href="item.Source"
              target="_blank"
              rel="nofollow noopener noreferrer"
              class="align-middle inline-block truncate max-w-64 text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-md hover:bg-gray-200 hover:text-gray-700 transition-colors"
            >
              <TIcon
                :icon="['fal', 'external-link']"
                size="sm"
                class="mr-1"
              />
              {{ formatSourceUrl(item.Source) }}
            </a>
          </p>
        </TListItem>
      </TListContainer>
    </TContainer>

    <TContainer
      v-if="data?.topHosts?.length"
      size="xl"
    >
      <LazyRvRentalsTopHostsSection
        :hosts="data.topHosts"
        :location-title="t('near', { locationTitle })"
      />
    </TContainer>

    <TContainer
      size="xl"
      class="space-y-16"
    >
      <RvRentalsCampgroundDelivery
        :campgrounds="data?.nearbyCampgrounds?.Data?.map((campground) => ({
          name: campground.Name,
          id: campground.Id,
        })) ?? []"
      />

      <div class="flex justify-center">
        <TButton
          variant="highlight"
          @click="navigateToRvSearch({ Delivery: true })"
        >
          {{ t('viewDeliverableRVs') }}
        </TButton>
      </div>
    </TContainer>

    <div
      v-if="data?.park?.Latitude && data?.park?.Longitude"
      class="space-y-8"
    >
      <TContainer size="xl">
        <THeading
          :level="3"
          tag="h2"
        >
          {{ t('rvsMaptitle', { name: data?.park?.Name }) }}
        </THeading>
      </TContainer>

      <TContainer size="2xl">
        <RvRentalsMap
          :center="{ lat: data?.park?.Latitude, lng: data?.park?.Longitude }"
          :zoom="10"
          :rvs="data?.rvs ?? []"
          :campgrounds="data?.nearbyCampgrounds?.Data ?? []"
        />
      </TContainer>

      <TContainer
        size="xl"
        class="space-y-4"
      >
        <p class="text-center font-medium">
          {{ parkNameWithLocation }}
        </p>

        <div
          v-if="data?.park?.HowToGetThere"
          class="space-y-2"
        >
          <THeading
            :level="5"
            tag="h3"
          >
            {{ t('howToGetThereTitle', { name: data?.park?.Name ?? '' }) }}
          </THeading>
          <p>
            {{ data.park.HowToGetThere }}
          </p>
        </div>
      </TContainer>
    </div>

    <TContainer size="2xl">
      <div class="bg-green-50 max-sm:-mx-4 sm:relative sm:left-1/2 sm:-translate-x-1/2 sm:w-dvw xl:static xl:left-auto xl:translate-x-0 xl:w-auto">
        <TContainer size="xl">
          <RvRentalsRvezyVsOthers class="py-12 md:py-16" />
        </TContainer>
      </div>
    </TContainer>

    <LazyRvRentalsFaqs
      v-if="data?.park?.FAQ?.length"
      :faqs="data?.park?.FAQ?.map((faq) => ({
        Id: faq.Id,
        Question: faq.Question ?? '',
        Answer: faq.Answer ?? '',
        Source: faq.Source ?? undefined,
      }))"
      :rv-title="t('anRVFor')"
      :location-title="data?.park?.Name ?? ''"
    />
  </div>
</template>

<script setup lang="ts">
import type { Breadcrumb } from '~/components/_tailwind/breadcrumb/types'
import type { COUNTRY_SHORTS } from '~/constants'
import { ROUTE_BASE_NAME } from '~/constants'

definePageMeta({
  layout: 'static',

  i18n: {
    paths: {
      en: '/rv-rentals/park/[parkSlug]',
      fr: '/locations-vr/parc/[parkSlug]',
    },
  },
})

const route = useRoute()
const { $rentals, $gis } = useNuxtApp()
const { locale, t } = useI18n()
const localePath = useLocalePath()
const { navigateToRvSearch } = useSearch()
const searchData = useSearchData()

const parkSlug = computed(() => Array.isArray(route.params.parkSlug) ? route.params.parkSlug.join('-') : route.params.parkSlug)

const { data } = await useAsyncData(
  computed(() => `rv-rentals-park-${parkSlug.value}-${locale.value}`),
  async () => {
    if (!parkSlug.value) {
      return { park: undefined }
    }

    const park = await $rentals('/api/parks/{parkSlug}', {
      path: {
        parkSlug: parkSlug.value ?? '',
      },
      query: {
        languageCode: locale.value,
      },
    })

    if (!park) {
      return { park }
    }

    const [gisData, nearbyRVs, topHosts, nearbyCampgrounds] = await Promise.all([
      $gis('/Geocoding/Geocode', {
        query: {
          Address: [park.Name, park.Region, park.Country].filter(Boolean).join(', '),
        },
      }),

      $rentals('/api/rvlistings/unified-search', {
        query: {
          SearchLat: park.Latitude,
          SearchLng: park.Longitude,
          Country: park.Country,
          PageSize: 50,
          SearchDistance: 200,
          IncludeFeatured: false,
          SortOrder: 'DistanceASC',
        },
      }),

      $rentals('/api/hosts/top-hosts', {
        query: {
          Latitude: park.Latitude,
          Longitude: park.Longitude,
          RadiusKm: 50,
          Limit: 2,
        },
      }),

      $rentals('/api/campgrounds', {
        query: {
          PageSize: 20,
          Latitude: park.Latitude,
          Longitude: park.Longitude,
          Country: park.Country as COUNTRY_SHORTS,
          Language: lookupLocaleToLanguage(locale.value),
          DistanceKm: 100,
          SortBy: 'DistanceAsc',
        },
      }),
    ])

    return {
      park,
      gisData,
      rvs: nearbyRVs?.PopularRVs?.ListRVs ?? [],
      topHosts,
      nearbyCampgrounds,
    }
  },
)

if (!data.value?.park) {
  throw createError({
    status: 404,
    statusText: 'Park not found',
    fatal: true,
  })
}
const { prePopulateSearchLocation } = useLocationPredictions()
const { updateSearchFormLocation } = useSearchForm()

onMounted(() => {
  const placeId = data.value?.gisData?.placeId
  const locationText = data.value?.park?.Name ?? data.value?.gisData?.name ?? ''

  prePopulateSearchLocation(locationText)

  if (placeId) {
    updateSearchFormLocation({
      placeId,
      text: locationText,
    })
    searchData.value.location = data.value?.gisData
  }
})

function i18nParams(lang: SupportedLocales) {
  if (locale.value === lang) {
    return { parkSlug: parkSlug.value }
  }

  const alternate = data.value?.park?.AlternateSlug
  if (alternate) {
    return { parkSlug: alternate }
  }

  return false
}

const setI18nParams = useSetI18nParams()
setI18nParams({
  en: i18nParams('en'),
  fr: i18nParams('fr'),
})

// Clear localized dynamic params before global i18n middleware runs; otherwise
// switchLocalePath merges stale eventSlug into unrelated routes (e.g. index) and Vue Router warns.
onBeforeRouteLeave(() => {
  setI18nParams({})
})

const { getStaticImage, presets } = useImage()

const parkMosaicImages = computed(() => {
  if (!data.value?.park?.Images?.length) {
    return []
  }

  const preset = presets.LANDSCAPE_LG

  return data.value.park.Images.map((image) => {
    const url = getStaticImage({
      path: image.Path,
      preset,
    }) ?? ''

    return {
      type: 'image' as const,
      path: image.Path,
      src: url,
      isStatic: true,
      description: image.AltText ?? data.value?.park?.Name ?? '',
      width: preset.width,
      height: preset.height,
    }
  })
})

useSeoHeadTags({
  title: computed(() => data.value?.park?.MetaTitle ?? ''),
  description: computed(() => data.value?.park?.MetaDescription ?? ''),
  image: computed(() => data.value?.park?.MetaImage ? getStaticImage({ path: data.value?.park?.MetaImage, preset: presets.LANDSCAPE_LG }) : parkMosaicImages.value[0]?.src),
})

const locationTitle = computed(() =>
  [data.value?.gisData?.regionCode, data.value?.gisData?.country].filter(Boolean).join(', '),
)

const parkNameWithLocation = computed(() => {
  return [data.value?.park?.Name, locationTitle.value].filter(Boolean).join(', ')
})

const pageHeading = computed(() => {
  const name = data.value?.park?.Name ?? ''
  const country = data.value?.gisData?.country
  return country ? `${name} - ${country}` : name
})

const breadcrumbs = computed(() => {
  const items: Breadcrumb[] = [
    {
      text: t('pageLink.rvRentals'),
      to: localePath({
        name: ROUTE_BASE_NAME.rv_rentals,
      }),
    }]

  const gisData = data.value?.gisData
  const pathSegments: { text: string, slug: string }[] = []

  if (gisData?.country) {
    pathSegments.push({ text: gisData.country, slug: formatAsSlug(gisData.country) })
  }
  if (gisData?.region) {
    pathSegments.push({ text: gisData.region, slug: formatAsSlug(gisData.region) })
  }

  const destinationPath: string[] = []
  for (const segment of pathSegments) {
    destinationPath.push(segment.slug)
    items.push({
      text: segment.text,
      to: localePath({
        name: ROUTE_BASE_NAME.rv_rentals_destination,
        params: { keyword: undefined, destinationPath: [...destinationPath] },
      }),
    })
  }

  items.push({
    text: data.value?.park?.Name ?? '',
    to: route.path,
  })

  return items
})

const pageSize = 9
const gridRvs = data.value?.rvs?.slice(0, pageSize) ?? []

const { queueViewableImpression } = useRvViewableImpressions({
  pageNumber: 1,
  pageSize,
})

const sortedWeather = computed(() =>
  [...(data.value?.park?.Weather ?? [])].sort((a, b) => a.Month - b.Month),
)

const { defineTouristAttraction } = useAugmentedSchemaOrg()
const absoluteLocalePath = useAbsoluteLocalePath()
const runtimeConfig = useRuntimeConfig()

const BASE_URL = runtimeConfig.public.baseURL
const attractionId = '#attraction'

function asSchemaOrgNode<T extends object>(node: T) {
  return node as T & Record<string, unknown>
}

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      {
        text: t('pageLink.home'),
        to: localePath({ name: 'index' }),
      },
      ...breadcrumbs.value,
    ].map((breadcrumb) => ({
      name: breadcrumb.text,
      item: breadcrumb.to,
    })),
  }),

  defineWebPage({
    mainEntity: { '@id': `${BASE_URL}/${attractionId}` },
  }),

  asSchemaOrgNode(defineTouristAttraction({
    id: attractionId,
    name: data.value?.park?.Name ?? '',
    url: absoluteLocalePath({ name: ROUTE_BASE_NAME.rv_rentals_park, params: { parkSlug: data.value?.park?.Slug } }),
    latitude: data.value?.park?.Latitude,
    longitude: data.value?.park?.Longitude,
    image: data.value?.park?.Images?.[0]?.Path,
    description: data.value?.park?.Description ?? undefined,
    city: data.value?.park?.City ?? undefined,
    region: data.value?.park?.Region ?? undefined,
    countryCode: data.value?.park?.Country,
    amenityFeature: data.value?.park?.RvFacts?.map((fact) => ({ name: fact.Label, value: fact.Value })),
  })),
])
</script>

<i18n lang="json">
  {
    "en": {
      "subtitle": "Discover the RVezy RV rental marketplace",
      "rvsGridTitle": "Featured RV rentals for {parkName}",
      "rvsMaptitle": "RV Rentals {name}",
      "browseRVRentals": "Browse RV Rentals",
      "viewDeliverableRVs": "View deliverable RVs",
      "anRVFor": "an RV for",
      "near": "near {locationTitle}",
      "aboutTitle": "About {name}",
      "rvFactsTitle": "RV quick facts for {name}",
      "rvFactsLabel": "Detail",
      "rvFactsValue": "Information",
      "howToGetThereTitle": "How to get to {name}",
      "bestTimeToVisitTitle": "Best time to visit {name}",
      "thingsToDoTitle": "Things to do at {name}",
      "thingsToDoRvParking": "RV parking available"
    },
    "fr": {
      "subtitle": "Découvrez la plateforme de location de VRs entre particuliers RVezy",
      "rvsGridTitle": "Locations de VR en vedette pour {parkName}",
      "rvsMaptitle": "VRs en location {name}",
      "browseRVRentals": "Parcourir les VRs",
      "viewDeliverableRVs": "Voir les VRs livrables",
      "anRVFor": "un VR pour",
      "near": "près de {locationTitle}",
      "aboutTitle": "À propos de {name}",
      "rvFactsTitle": "Infos VR pour {name}",
      "rvFactsLabel": "Détail",
      "rvFactsValue": "Information",
      "howToGetThereTitle": "Comment se rendre à {name}",
      "bestTimeToVisitTitle": "Meilleur moment pour visiter {name}",
      "thingsToDoTitle": "Que faire à {name}",
      "thingsToDoRvParking": "Stationnement VR disponible"
    }
  }
</i18n>
